import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import {
  CONSOLE_DAY_FULL,
  getCityFromSearchParams,
  getConsoleCityUrl,
  getConsoleMeetingsRangeFromSearchParams,
} from "../../utils/consoleUtils";
import "../../styles/console.css";

/**
 * @param {object} [props]
 * @param {object|null} [props.qualityData] - From parent ConsoleLayout (single quality-checks fetch per city)
 * @param {unknown} [props.qualityError]
 * @param {string|null} [props.recurringQualityUtcRange] - Preformatted onboarding recurring window (from parent; avoids duplicate work)
 */
export default function OverviewView({
  qualityData = null,
  qualityError = null,
  recurringQualityUtcRange = null,
} = {}) {
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const meetingsRange = getConsoleMeetingsRangeFromSearchParams(searchParams);

  const { data, loading, error } = useConsoleFetch(
    () =>
      cityId
        ? consoleApi.stats.coverage(cityId, { range_key: meetingsRange })
        : Promise.resolve(null),
    [cityId, meetingsRange]
  );

  return (
    <ConsoleViewShell loading={loading} error={error} loadingLabel="Loading overview…">
      {!cityId && (
        <div className="console-view">
          <p className="console-muted-text">Select a city to view overview.</p>
        </div>
      )}
      {cityId && data && (
        <div className="console-main-inner">
          <ConsolePageHeader
            label="Workspace"
            title="Overview"
            sub="Current city status, coverage, and next moves. All data is from the live coverage API."
          />

          {/* Focus band — from coverage API only */}
          <div className="focus-band">
            <div className="focus-band-card">
              <div className="focus-band-label">Current city focus</div>
              <div className="focus-band-value">{data.city_name ?? ""}</div>
            </div>
            <div className="focus-band-card">
              <div className="focus-band-label">City phase</div>
              <div className="focus-band-value">{data.phase != null ? data.phase : ""}</div>
            </div>
            <div className="focus-band-card">
              <div className="focus-band-label">Coverage</div>
              <div className="focus-band-value subtle">
                {data.health_banner?.covered_slots != null
                  ? `${data.health_banner.covered_slots}/14`
                  : ""}
              </div>
            </div>
            <div className="focus-band-card">
              <div className="focus-band-label">Fragile slots</div>
              <div className="focus-band-value">
                {typeof data.stability_forecast?.fragile_count === "number"
                  ? String(data.stability_forecast.fragile_count)
                  : ""}
              </div>
            </div>
            <div className="focus-band-card">
              <div className="focus-band-label">Next leverage move</div>
              <div className="focus-band-value">{data.next_leverage_move ?? ""}</div>
            </div>
          </div>

          {/* Metrics grid — from coverage API only */}
          <div className="section-label">Product status</div>
          <div className="metrics-grid">
            {data.coverage_score != null && (
              <div className="mc">
                <div className="mc-label">Coverage score</div>
                <div className="mc-val">{data.coverage_score}%</div>
                <div className="mc-sub">Target 12/14 slots</div>
              </div>
            )}
            {data.health_banner?.covered_slots != null && (
              <div className="mc">
                <div className="mc-label">Slots filled</div>
                <div className="mc-val">{data.health_banner.covered_slots}/14</div>
                <div className="mc-sub">Weekly rhythm</div>
              </div>
            )}
            {data.health_banner?.anchor_slots_covered != null && (
              <div className="mc">
                <div className="mc-label">Anchor slots</div>
                <div className="mc-val">{data.health_banner.anchor_slots_covered}/6</div>
                <div className="mc-sub">Key slots covered</div>
              </div>
            )}
            {data.total_runs != null && (
              <div className="mc">
                <div className="mc-label">Total runs</div>
                <div className="mc-val">{data.total_runs}</div>
                <div className="mc-sub">Published in city</div>
              </div>
            )}
          </div>

          {/* City Rhythm — from data.city_rhythm only */}
          {data.city_rhythm && (
            <>
              <div className="section-label">
                City rhythm{data.city_name ? ` — ${data.city_name}` : ""}
              </div>
              <div className="crp-wrap">
                <div className="crp-grid-card">
                  <div className="crp-score-card">
                    <div className="crp-score-row">
                      <span className="crp-score-label">Coverage</span>
                      <span className="crp-score-val">
                        {data.coverage_score != null && (
                          <>
                            {data.coverage_score}
                            <span className="crp-score-denom">%</span>
                          </>
                        )}
                      </span>
                    </div>
                    {data.health_banner?.covered_slots != null && (
                      <div className="crp-score-bar">
                        <div
                          className="crp-score-fill"
                          style={{
                            width: `${Math.min(
                              100,
                              (data.health_banner.covered_slots / 14) * 100
                            )}%`,
                            background: "var(--accent)",
                          }}
                        />
                      </div>
                    )}
                    <div className="crp-score-target">14 weekly slots</div>
                  </div>
                </div>
                <div className="crp-side">
                  <div className="crp-phase-card">
                    <div className="crp-phase-title">City rhythm</div>
                    {data.city_rhythm.most_active_day != null && (
                      <div className="crp-milestone">
                        <span className="crp-ms-dot active" />
                        <span className="crp-ms-text">
                          <strong>Most active:</strong>{" "}
                          {CONSOLE_DAY_FULL[data.city_rhythm.most_active_day] ??
                            data.city_rhythm.most_active_day}
                        </span>
                      </div>
                    )}
                    {data.city_rhythm.weakest_slot && (
                      <div className="crp-milestone">
                        <span className="crp-ms-dot future" />
                        <span className="crp-ms-text">
                          <strong>Weakest slot:</strong>{" "}
                          {data.city_rhythm.weakest_slot.day}{" "}
                          {data.city_rhythm.weakest_slot.slot != null
                            ? String(data.city_rhythm.weakest_slot.slot).toUpperCase()
                            : ""}
                        </span>
                      </div>
                    )}
                    {data.city_rhythm.anchor_run_count != null && (
                      <div className="crp-milestone">
                        <span className="crp-ms-dot done" />
                        <span className="crp-ms-text">
                          <strong>Anchor runs:</strong> {data.city_rhythm.anchor_run_count}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Onboarding Progress Panel — SOP-backed completion tracking */}
          {qualityData && !qualityError && (
            <div className="onboarding-progress-panel">
              <div className="section-label">Onboarding Progress</div>
              <div className="onboarding-progress-grid">
                <div className="onboarding-progress-item">
                  <div className="onboarding-progress-label">Anchor Clubs</div>
                  <div className="onboarding-progress-value">
                    {qualityData.onboarding_progress?.anchor_clubs_count ?? 0} / {qualityData.onboarding_progress?.anchor_clubs_target ?? 6}
                  </div>
                  <div className="onboarding-progress-bar">
                    <div
                      className="onboarding-progress-fill"
                      style={{
                        width: `${Math.min(
                          100,
                          ((qualityData.onboarding_progress?.anchor_clubs_count ?? 0) /
                            (qualityData.onboarding_progress?.anchor_clubs_target ?? 6)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="onboarding-progress-item">
                  <div className="onboarding-progress-label">Recurring runs</div>
                  <div className="onboarding-progress-sublabel" style={{ fontSize: 11, color: "var(--console-muted)", marginBottom: 4 }}>
                    Distinct series with a visible occurrence in the next 7 days
                    {recurringQualityUtcRange != null && recurringQualityUtcRange !== "" ? (
                      <>
                        <br />
                        <span style={{ fontWeight: 500, color: "var(--console-ink)" }}>{recurringQualityUtcRange}</span>
                      </>
                    ) : null}
                  </div>
                  <div className="onboarding-progress-value">
                    {qualityData.onboarding_progress?.recurring_runs_count ?? 0} / {qualityData.onboarding_progress?.recurring_runs_target ?? 50}
                  </div>
                  <div className="onboarding-progress-bar">
                    <div
                      className="onboarding-progress-fill"
                      style={{
                        width: `${Math.min(
                          100,
                          ((qualityData.onboarding_progress?.recurring_runs_count ?? 0) /
                            (qualityData.onboarding_progress?.recurring_runs_target ?? 50)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="onboarding-progress-item">
                  <div className="onboarding-progress-label">Coverage Slots</div>
                  <div className="onboarding-progress-value">
                    {qualityData.onboarding_progress?.coverage_slots_covered ?? 0} / {qualityData.onboarding_progress?.coverage_slots_target ?? 8}
                  </div>
                  <div className="onboarding-progress-bar">
                    <div
                      className="onboarding-progress-fill"
                      style={{
                        width: `${Math.min(
                          100,
                          ((qualityData.onboarding_progress?.coverage_slots_covered ?? 0) /
                            (qualityData.onboarding_progress?.coverage_slots_target ?? 8)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="onboarding-progress-item">
                  <div className="onboarding-progress-label">Editorial Photos</div>
                  <div className="onboarding-progress-value">
                    {qualityData.onboarding_progress?.editorial_photos_complete ? "✓ Complete" : "⚠ Incomplete"}
                  </div>
                </div>
              </div>
              {Array.isArray(qualityData.hard_blockers) && qualityData.hard_blockers.length > 0 && (
                <div className="onboarding-blockers">
                  <div className="onboarding-blockers-title">Hard Blockers (Must Fix)</div>
                  {qualityData.hard_blockers.map((blocker, i) => (
                    <div key={i} className="onboarding-blocker-item">
                      <span className="onboarding-blocker-icon">✗</span>
                      <div>
                        <div className="onboarding-blocker-title">{blocker.title}</div>
                        <div className="onboarding-blocker-desc">{blocker.description}</div>
                        {/recurring/i.test(blocker.title ?? "") &&
                          recurringQualityUtcRange != null &&
                          recurringQualityUtcRange !== "" && (
                            <div className="onboarding-blocker-desc" style={{ marginTop: 6, fontSize: 12 }}>
                              Window: {recurringQualityUtcRange}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(qualityData.soft_warnings) && qualityData.soft_warnings.length > 0 && (
                <div className="onboarding-warnings">
                  <div className="onboarding-warnings-title">Soft Warnings (Should Fix)</div>
                  {qualityData.soft_warnings.map((warning, i) => (
                    <div key={i} className="onboarding-warning-item">
                      <span className="onboarding-warning-icon">⚠</span>
                      <div>
                        <div className="onboarding-warning-title">{warning.title}</div>
                        <div className="onboarding-warning-desc">{warning.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Generative City — static product copy from reference */}
          <div className="gen-city-card">
            <div className="gen-city-title">Generative City</div>
            <div className="gen-city-body">
              <p>When a coverage grid becomes visible, new runs tend to appear because gaps become obvious to runners and clubs.</p>
              <p>RunCards does not create runs. It reveals the structure that allows them.</p>
            </div>
          </div>

          {/* Next moves — link to Coverage for full grid */}
          <div className="section-label">Next steps</div>
          <div className="focus-card">
            <div className="fc-eyebrow">Operator actions</div>
            {Array.isArray(data.operator_tasks) && data.operator_tasks.length > 0 ? (
              <div>
                {data.operator_tasks.slice(0, 5).map((task, i) => {
                  const title = typeof task === "string" ? task : task?.title;
                  const description = typeof task === "object" && task != null ? task.description : null;
                  return (
                    <div key={i} className="focus-row">
                      <span className="frl">
                        {title}
                        {description != null && description !== "" && (
                          <> — {description}</>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="focus-row">
                <span className="frl">View Coverage tab for the slot grid and priorities.</span>
                <Link to={getConsoleCityUrl("coverage", cityId)} className="tb-btn">
                  Open Coverage
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </ConsoleViewShell>
  );
}
